import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PropertyData } from '../types';
import { config } from '../config';
import type { WidgetConfig, WidgetTrigger } from '@lasoxp/widget';
import { devError } from '../utils/dev-log';

interface WidgetInstance {
  prefillMessage?: (message: string, autoOpen?: boolean) => void;
}

interface IframeOverlayProps {
  url: string;
  propertyData: PropertyData | null;
  suggestions?: string[];
}

export default function IframeOverlay({
  url,
  propertyData,
  suggestions,
}: IframeOverlayProps) {
  const widgetInitializedRef = useRef(false);
  const [widgetError, setWidgetError] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [iframeChecking, setIframeChecking] = useState(true);
  const [useProxy, setUseProxy] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isLocalWidgetHost =
    config.widgetUrl.includes('localhost') ||
    config.widgetUrl.includes('127.0.0.1') ||
    config.widgetUrl.includes('.dev.lasoxp.com');
  // The ?v= query busts year-long browser caches poisoned before the bundle
  // was switched to Cache-Control: no-cache (2026-06-09) — those devices never
  // revalidate the bare URL. Bump when a broken bundle must be evicted.
  const widgetScriptUrl = isLocalWidgetHost
    ? `${config.widgetUrl}/src/index.ts`
    : `${config.widgetUrl}/lasoxp-widget.umd.js?v=2`;

  // Reset widget initialization when property changes
  useEffect(() => {
    widgetInitializedRef.current = false;
    setWidgetError(false);
  }, [propertyData?.id]);

  // Initialize the actual widget when property data is available
  useEffect(() => {
    if (!propertyData || widgetInitializedRef.current) {
      return;
    }

    if (document.querySelector('.lasoxp-widget-container')) {
      widgetInitializedRef.current = true;
      return;
    }

    const defaultSuggestions = suggestions || [
      'Tell me about amenities',
      'What activities are available?',
      'Room information',
      'Contact details',
    ];

    // Use property's saved widget config when available, with sensible demo defaults
    const savedWidget = propertyData.widgetConfig ?? {};

    // Build triggers: use server-configured triggers if available, otherwise sensible demo defaults
    const serverTriggers = (savedWidget.triggers ?? []).filter((t) => t.enabled !== false);
    const demoTriggers: WidgetTrigger[] = serverTriggers.length > 0
      ? serverTriggers as WidgetTrigger[]
      : [
          {
            id: 'demo-default-time',
            type: 'time' as const,
            value: 5000,
            message: `Welcome to ${propertyData.name || 'our property'}! I'm your virtual concierge — anything I can help you discover?`,
          },
        ];

    const createConfig = (overrides?: Partial<WidgetConfig>): WidgetConfig => ({
      propertyId: propertyData.id || 'demo-property-123',
      apiUrl: config.apiUrl,
      wsUrl: config.wsUrl,
      firebaseConfig: config.firebase,
      theme: (savedWidget.theme as WidgetConfig['theme']) ?? 'light',
      position: (savedWidget.position as WidgetConfig['position']) ?? 'bottom-right',
      primaryColor: savedWidget.primaryColor || '#3B82F6',
      welcomeMessage: savedWidget.greetingMessage ||
        `Welcome to ${propertyData.name || 'our property'}! I'm here to help you discover our amenities, activities, and answer any questions. How can I assist you today?`,
      initialSuggestions: defaultSuggestions,
      companyName: propertyData.name || 'LasoXP Property',
      enableEmailCapture: savedWidget.enableLeadCapture !== false,
      enableAuthentication: true,
      enableHumanHandover: true,
      showBranding: savedWidget.showBranding !== false,
      // When triggers are configured, start closed so they can fire naturally;
      // otherwise auto-open to show the widget immediately for prospects
      autoOpen: serverTriggers.length === 0,
      triggers: demoTriggers,
      onLeadCapture: () => {},
      onHumanHandover: () => {},
      ...overrides,
    });

    const script = document.createElement('script');
    script.src = widgetScriptUrl;
    script.async = true;
    script.setAttribute('data-lasoxp-widget', 'true');

    // For local dev with Vite, we need type="module" to support import.meta
    if (isLocalWidgetHost && widgetScriptUrl.endsWith('.ts')) {
      script.type = 'module';
    }

    script.onload = () => {
      const initWidget = (
        window as typeof window & {
          initLasoXPWidget?: (config: WidgetConfig) => WidgetInstance | void;
        }
      ).initLasoXPWidget;

      if (typeof initWidget === 'function') {
        initWidget(createConfig());
        widgetInitializedRef.current = true;
      } else {
        setWidgetError(true);
      }
    };

    script.onerror = () => {
      setWidgetError(true);
    };

    document.head.appendChild(script);

    return () => {
      script.remove();
      document
        .querySelectorAll('.lasoxp-widget-container')
        .forEach((el) => el.remove());
      widgetInitializedRef.current = false;
    };
    // Only re-init when the property or widget script changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData?.id, widgetScriptUrl]);

  // Check iframe compatibility server-side when URL changes
  useEffect(() => {
    const checkIframeCompatibility = async () => {
      setIframeChecking(true);
      setIframeError(false);
      setUseProxy(false);

      try {
        const response = await fetch(`${config.apiUrl}/v1/iframe/check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url }),
        });

        const result = await response.json();

        if (!result.canEmbed) {
          setUseProxy(true);
        }
      } catch (error) {
        devError('Error checking iframe compatibility:', error);
      } finally {
        setIframeChecking(false);
      }
    };

    if (url) {
      checkIframeCompatibility();
    }
  }, [url]);

  const handleIframeLoad = () => {
    setIframeError(false);
  };

  const handleIframeError = () => {
    setIframeError(true);
  };

  const getIframeSrc = () => {
    if (useProxy) {
      return `${config.apiUrl}/v1/iframe/proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  return (
    // overflow-hidden: iOS Safari expands iframes to their content's intrinsic
    // height regardless of CSS, blowing the document out past the viewport and
    // breaking hit-testing on fixed overlays (the chat bubble stops responding
    // to taps). Clipping here + overflow-auto on the iframe wrapper keeps the
    // page viewport-sized while the embedded site stays scrollable.
    <div className="relative w-full h-screen overflow-hidden">
      {/* Loading state */}
      {iframeChecking && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Checking Website Compatibility
            </h2>
            <p className="text-gray-600">
              Verifying if this website can be embedded...
            </p>
          </div>
        </div>
      )}

      {/* Iframe */}
      {!iframeChecking &&
        (iframeError ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Preview Not Available
              </h2>
              <p className="text-gray-600 mb-6 max-w-md">
                This website has security settings that prevent it from being
                displayed here. You can still use the chat widget.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Open Website in New Tab
              </a>
            </div>
          </div>
        ) : (
          <div className="relative w-full h-full overflow-auto">
            {useProxy && (
              <div className="absolute top-4 right-4 bg-yellow-100 border border-yellow-300 rounded-lg px-3 py-2 text-sm text-yellow-800 z-10">
                Using proxy mode
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={getIframeSrc()}
              className="w-full h-full border-0"
              title="Property Website"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          </div>
        ))}

      {/* Widget error fallback */}
      {widgetError && (
        <div className="fixed bottom-6 right-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 shadow-lg z-50">
          Chat widget failed to load.
        </div>
      )}

      {/* Demo Indicator Badge */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="fixed top-6 left-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg pointer-events-auto"
        >
          <div className="flex items-center gap-2">
            LasoXP Agent Demo
          </div>
        </motion.div>
      </div>
    </div>
  );
}
